/**
 * An NX server's two logins, in words (Settings › Hosts).
 *
 *   monitoring — the asset's own credential (064): what the agent logs in as
 *     every minute. Should be view-only.
 *   elevated — a Power User credential (065), handed out only for an NX
 *     action an admin asks for.
 *
 * Moorebank, 2026-10-20: three servers of one site, an `element` login set as
 * the ELEVATED login on some and attached as the monitoring login on none, and
 * the survey saying only "login: HTTP 404". So this says which user was
 * tried, from which credential, and groups one site's servers so they can be
 * given the same login at once. Tested in test/nx_logins_screen.test.ts.
 */

export interface LoginName {
  credentialId: string;
  label: string;
  username: string | null;
}

export interface HostLogins {
  monitoring: LoginName | null;
  elevated: LoginName | null;
}

export interface LoginHost {
  id: string;
  code: string | null;
  name: string;
  agentId: string | null;
  nx: {
    siteId?: string | null;
    siteName?: string | null;
    login: 'none' | 'ok' | 'failed' | 'cert_changed';
    /** Which NX user that state is for (newer agents); absent or null from older ones. */
    loginUser?: string | null;
    error: string | null;
  } | null;
  nxLogins: HostLogins;
}

const who = (l: LoginName): string => `${l.username ?? '(no username)'} (credential “${l.label}”)`;

/**
 * Which NX site a server belongs to: NX's own site id when the agent sends it
 * (newer agents), else the site's name. Null when it says neither.
 */
export function siteKey(host: LoginHost): string | null {
  const id = host.nx?.siteId?.trim();
  if (id) return `id:${id.toLowerCase()}`;
  const name = host.nx?.siteName?.trim();
  return name ? `name:${name.toLowerCase()}` : null;
}

export interface SiteGroup<T extends LoginHost> {
  key: string;
  name: string;
  hosts: T[];
  /** How many have no monitoring login at all. */
  missing: number;
  /** Not every server logs in with the same credential. */
  mixed: boolean;
}

/** NX sites with more than one server here, in name order. */
export function siteGroups<T extends LoginHost>(hosts: T[]): SiteGroup<T>[] {
  const by = new Map<string, T[]>();
  for (const h of hosts) {
    const key = siteKey(h);
    if (key === null) continue;
    by.set(key, [...(by.get(key) ?? []), h]);
  }
  return [...by.entries()]
    .filter(([, list]) => list.length > 1)
    .map(([key, list]) => {
      const ids = new Set(list.map((h) => h.nxLogins.monitoring?.credentialId ?? ''));
      return {
        key,
        name: list.find((h) => h.nx?.siteName)?.nx?.siteName ?? 'this NX site',
        hosts: list,
        missing: list.filter((h) => h.nxLogins.monitoring === null).length,
        mixed: ids.size > 1,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** The other servers on the same NX site as `host`. */
export function siteMates<T extends LoginHost>(host: T, hosts: T[]): T[] {
  const key = siteKey(host);
  return key === null ? [] : hosts.filter((h) => h.id !== host.id && siteKey(h) === key);
}

/** One line for the Hosts row: who the agent logs in as, and whether NX takes it. */
export function loginSummary(host: LoginHost): { text: string; problem: boolean } {
  const m = host.nxLogins.monitoring;
  const nx = host.nx;
  if (m === null) {
    return { text: 'No monitoring login: choose one so CosMon can read NX', problem: true };
  }
  if (nx === null) return { text: `Monitoring login: ${who(m)}`, problem: false };
  // Just changed: the state is still the old user's. Never pin it on the new one.
  if (nx.loginUser != null && nx.loginUser !== '' && m.username !== null && nx.loginUser !== m.username) {
    return { text: `Monitoring login: ${who(m)}, not tried yet (the last check was as ${nx.loginUser})`, problem: false };
  }
  switch (nx.login) {
    case 'ok':
      return { text: `Logs in as ${who(m)}`, problem: false };
    case 'failed':
      return { text: `NX refused ${who(m)}${nx.error ? `: ${nx.error}` : ''}`, problem: true };
    case 'cert_changed':
      return { text: `Login as ${who(m)} not sent: NX’s certificate changed`, problem: true };
    default:
      // Attached, and the agent has not tried it yet (it does within a report or two).
      return { text: `Monitoring login: ${who(m)}, not tried yet`, problem: false };
  }
}

export interface ResultLogin {
  /** The NX user the agent tried; null when the result does not say. */
  user: string | null;
  /** monitoring or elevated: which of the two it was. */
  tier: 'monitoring' | 'elevated';
  /** The credential CosMon held for that tier when the result arrived. */
  from: LoginName | null;
  /** ok, failed: …, none: … — as the agent put it. */
  outcome: string | null;
  failed: boolean;
  /** Set when the host's login has been changed since: say the result is old. */
  changedSince: string | null;
}

/**
 * Which login a survey or action result was made with, from the agent's own
 * `_survey` / `_action` and the `_cosmon` names stamped on arrival. `now` is
 * what the host has attached today, to say when that has moved on.
 */
export function resultLogin(result: Record<string, unknown>, now: HostLogins | null): ResultLogin {
  const action = result['_action'] as { login_used?: string; login_user?: string } | undefined;
  const survey = result['_survey'] as { login_user?: string } | undefined;
  const tier: ResultLogin['tier'] = action?.login_used === 'elevated' ? 'elevated' : 'monitoring';
  const user = (action?.login_user ?? survey?.login_user ?? '') || null;
  const stamped = (result['_cosmon'] as { logins?: Partial<HostLogins> } | undefined)?.logins?.[tier] ?? null;
  const outcome = typeof result['_login'] === 'string' ? (result['_login'] as string) : null;
  const current = now?.[tier] ?? null;
  let changedSince: string | null = null;
  if (now !== null) {
    if (stamped !== null && current?.credentialId !== stamped.credentialId) {
      changedSince = current === null ? `The ${tier} login has been detached since.` : `The ${tier} login is now ${who(current)}.`;
    } else if (stamped === null && user !== null && current !== null && current.username !== user) {
      changedSince = `The ${tier} login is now ${who(current)}.`;
    }
  }
  return { user, tier, from: stamped, outcome, failed: outcome?.startsWith('failed') === true || outcome?.startsWith('none') === true, changedSince };
}

/** A host as the Hosts page holds it, with auto-add for its NX site (null: its agent says no site). */
export type SiteHost = LoginHost & { nxAutoAdd?: boolean | null };

export interface NxSite<T extends SiteHost> {
  key: string;
  /** NX's own id, what the setup route takes; null when only an older agent's site name is known. */
  siteId: string | null;
  name: string;
  hosts: T[];
  /** No monitoring login at all. */
  missing: number;
  /** The monitoring login works (as the agent last said, for that user). */
  ok: number;
  /** NX refused the login the host holds now. */
  refused: number;
  /** A login is attached and the agent has not tried it yet. */
  untried: number;
  /** Not every server logs in with the same credential. */
  mixed: boolean;
  autoAdd: boolean;
}

/** Whether the login state the agent reports is about the credential the host holds now. */
function tried(h: LoginHost): boolean {
  const m = h.nxLogins.monitoring;
  const nx = h.nx;
  if (m === null || nx === null) return false;
  if (nx.loginUser != null && nx.loginUser !== '' && m.username !== null && nx.loginUser !== m.username) return false;
  return nx.login === 'ok' || nx.login === 'failed';
}

/**
 * Every NX site any host reports, one server or many, set up or not (Add NX
 * site, 2026-10-20). Since 2026-10-21 each one is shown in the header of its
 * site on the Hosts page's By site view (`hosts-view.ts`), where it is set up
 * and where its state is read afterwards.
 */
export function nxSites<T extends SiteHost>(hosts: T[]): NxSite<T>[] {
  const by = new Map<string, T[]>();
  for (const h of hosts) {
    const key = siteKey(h);
    if (key === null) continue;
    by.set(key, [...(by.get(key) ?? []), h]);
  }
  return [...by.entries()]
    .map(([key, list]) => {
      const ids = new Set(list.map((h) => h.nxLogins.monitoring?.credentialId ?? ''));
      return {
        key,
        siteId: list.find((h) => h.nx?.siteId?.trim())?.nx?.siteId?.trim().toLowerCase() ?? null,
        name: list.find((h) => h.nx?.siteName)?.nx?.siteName ?? 'this NX site',
        hosts: list,
        missing: list.filter((h) => h.nxLogins.monitoring === null).length,
        ok: list.filter((h) => tried(h) && h.nx?.login === 'ok').length,
        refused: list.filter((h) => tried(h) && h.nx?.login === 'failed').length,
        untried: list.filter((h) => h.nxLogins.monitoring !== null && !tried(h)).length,
        mixed: ids.size > 1,
        autoAdd: list.some((h) => h.nxAutoAdd === true),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** One line for the NX sites list: what, if anything, is wrong with the site. */
export function siteState<T extends SiteHost>(site: NxSite<T>): { text: string; tone: 'ok' | 'warn' | 'fault' } {
  const n = site.hosts.length;
  const servers = (k: number): string => `${k} server${k === 1 ? '' : 's'}`;
  const auto = `; auto-add ${site.autoAdd ? 'on' : 'off'}`;
  if (site.refused > 0) return { tone: 'fault', text: `NX refused the login on ${servers(site.refused)}${auto}` };
  if (site.missing > 0) {
    return { tone: 'warn', text: `${site.missing} of ${servers(n)} ${site.missing === 1 ? 'has' : 'have'} no monitoring login${auto}` };
  }
  if (site.untried > 0) return { tone: 'warn', text: `Login set on ${servers(site.untried)}, not tried yet${auto}` };
  if (site.mixed) return { tone: 'warn', text: `${servers(n)} log in with different credentials${auto}` };
  return { tone: 'ok', text: `${servers(n)} log${n === 1 ? 's' : ''} in${auto}` };
}
